/**
 * react-data-table-component customStyles — design-system v2.
 *
 * This file is the single source of truth for every legacy DataTable
 * across the app. Updating it here re-themes:
 *   - Asset List (when re-enabled) / Allocate / Passcode
 *   - User List (when re-enabled)
 *   - Task List, Trip List, Notification History
 *   - Customer List, Visiting Report, Tracking Report
 *   - GT06 reports (Alert Log, Daily Activity, Trips, Fleet
 *     Distance)
 *   - Audit Log
 *
 * Visual language:
 *   - Calm ink-50 header (no orange gradient) with uppercase 11px
 *     column titles in ink-500 — readable but quiet so data wins.
 *   - 44px rows, soft brand-50 hover, 12px ink-100 borders.
 *   - Selected rows pick up the accent-50 left bar treatment used
 *     across the redesign.
 *   - Tabular numerals + 14px body text consistent with the rest of
 *     the v2 chrome.
 *   - Header is sticky inside any overflow container.
 */
export const customStyles = {
  table: {
    style: {
      width: "100%",
      minWidth: "100%",
      backgroundColor: "#ffffff",
      borderRadius: "12px",
      border: "1px solid #e2e8f0", // ink-200
      boxShadow: "0 1px 2px 0 rgb(16 24 40 / 0.04)",
      overflow: "hidden",
    },
  },

  headRow: {
    style: {
      background: "#f8fafc", // ink-50
      minHeight: "40px",
      borderBottom: "1px solid #e2e8f0",
      position: "sticky",
      top: 0,
      zIndex: 2,
    },
  },

  headCells: {
    style: {
      color: "#64748b", // ink-500
      fontWeight: 600,
      fontSize: "11px",
      letterSpacing: "0.05em",
      textTransform: "uppercase",
      padding: "0 14px",
      whiteSpace: "nowrap",
      fontFamily: "inherit",
    },
  },

  rows: {
    style: {
      minHeight: "44px",
      backgroundColor: "#ffffff",
      borderBottom: "1px solid #f1f5f9", // ink-100
      transition: "background 0.12s ease",
      color: "#0f172a", // ink-900
      fontVariantNumeric: "tabular-nums",
      "&:not(:last-of-type)": { borderBottom: "1px solid #f1f5f9" },
      "&:hover": {
        backgroundColor: "#f3f1fb", // brand-50
        cursor: "pointer",
      },
    },
    stripedStyle: {
      backgroundColor: "#fafbfd", // surface-subtle
    },
    selectedHighlightStyle: {
      backgroundColor: "#fff5f2", // accent-50
      borderLeft: "2px solid #ff683a",
    },
  },

  cells: {
    style: {
      fontSize: "13px",
      lineHeight: "18px",
      padding: "10px 14px",
      color: "#0f172a",
      fontFamily: "inherit",
      whiteSpace: "normal",
      wordBreak: "break-word",
    },
  },

  pagination: {
    style: {
      borderTop: "1px solid #f1f5f9",
      padding: "8px 14px",
      backgroundColor: "#ffffff",
      fontSize: "12.5px",
      color: "#475569", // ink-600
      fontFamily: "inherit",
      borderRadius: "0 0 12px 12px",
      minHeight: "auto",
    },
    pageButtonsStyle: {
      borderRadius: "6px",
      height: "32px",
      width: "32px",
      padding: 0,
      margin: "0 2px",
      cursor: "pointer",
      transition: "background-color 0.12s ease",
      color: "#334155",
      fill: "#334155",
      "&:disabled": { color: "#cbd5e1", fill: "#cbd5e1" },
      "&:hover:not(:disabled)": { backgroundColor: "#f1f5f9" },
      "&:focus": { outline: "none", backgroundColor: "#f1f5f9" },
    },
  },

  noData: {
    style: {
      padding: "48px 16px",
      backgroundColor: "#ffffff",
      color: "#64748b",
    },
  },

  progress: {
    style: {
      backgroundColor: "#ffffff",
      padding: "32px",
    },
  },

  expandableRows: {
    style: {
      backgroundColor: "#fafbfd",
    },
  },

  expandableRowsContent: {
    style: {
      padding: "14px",
      fontSize: "13px",
      backgroundColor: "#ffffff",
    },
  },
};
